import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  KnowledgeLinkIngestResultItem,
  KnowledgeLinkIngestUpdatedEvent,
} from '@digital-twins/shared-types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { TFunction } from 'i18next'
import { knowledgeApi } from '@/lib/api'
import { onKnowledgeLinkIngestUpdated } from '@/lib/services/websocket'

const MAX_LINK_URLS = 10

export function parseLinkUrls(raw: string): Array<string> {
  const lines = raw
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean)
  const seen = new Set<string>()
  const out: Array<string> = []
  for (const line of lines) {
    const key = line.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    out.push(line)
  }
  return out
}

interface KnowledgeAddLinksModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDocumentsChanged: () => void
}

function statusLabel(
  item: KnowledgeLinkIngestResultItem,
  t: TFunction<'app'>,
): string {
  if (item.status === 'queued') {
    return t('library.statusQueued')
  }
  if (item.status === 'duplicate') {
    return t('library.statusDuplicate')
  }
  return item.failureReason
    ? t('library.statusRejectedReason', { reason: item.failureReason })
    : t('library.statusRejected')
}

export function KnowledgeAddLinksModal({
  open,
  onOpenChange,
  onDocumentsChanged,
}: KnowledgeAddLinksModalProps) {
  const { t } = useTranslation('app')
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [results, setResults] = useState<Array<KnowledgeLinkIngestResultItem>>([])
  const [pendingUrls, setPendingUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) {
      return
    }
    return onKnowledgeLinkIngestUpdated((event: KnowledgeLinkIngestUpdatedEvent) => {
      if (batchId && event.batchId !== batchId) {
        return
      }
      setPendingUrls((prev) => {
        if (!prev[event.url]) {
          return prev
        }
        const next = { ...prev }
        delete next[event.url]
        return next
      })
      if (event.status === 'created') {
        toast.success(t('library.toastLinkAdded'), {
          description: event.url,
        })
        onDocumentsChanged()
      } else {
        toast.error(t('library.toastIngestFailed'), {
          description: `${event.url}: ${event.failureReason || t('library.failedGeneric')}`,
        })
        setResults((prev) =>
          prev.map((row) =>
            row.url === event.url
              ? {
                  ...row,
                  status: 'rejected',
                  failureReason: event.failureReason || t('library.failedGeneric'),
                }
              : row,
          ),
        )
      }
    })
  }, [open, batchId, onDocumentsChanged, t])

  async function handleSubmit() {
    const urls = parseLinkUrls(text)
    if (urls.length === 0) {
      toast.error(t('library.toastPasteUrl'))
      return
    }
    if (urls.length > MAX_LINK_URLS) {
      toast.error(t('library.toastMaxLinks', { count: MAX_LINK_URLS }))
      return
    }

    setSubmitting(true)
    try {
      const response = await knowledgeApi.ingestUrls(urls)
      setBatchId(response.batchId)
      setResults(response.results)
      const pending: Record<string, string> = {}
      for (const row of response.results) {
        if (row.status === 'queued') {
          pending[row.url] = 'capturing'
        }
      }
      setPendingUrls(pending)
      const queued = response.results.filter((r) => r.status === 'queued').length
      const duplicates = response.results.filter((r) => r.status === 'duplicate').length
      const rejected = response.results.filter((r) => r.status === 'rejected').length
      if (queued > 0) {
        toast.message(
          t('library.toastQueued', { count: queued }),
          {
            description:
              duplicates || rejected
                ? t('library.toastQueuedDetail', { duplicates, rejected })
                : t('library.toastTranscribing'),
          },
        )
        onDocumentsChanged()
      } else if (duplicates > 0 && rejected === 0) {
        toast.message(t('library.toastAllDuplicates'))
      } else {
        toast.error(t('library.toastNoneQueued'))
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('library.toastAddFailed')
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setText('')
      setResults([])
      setPendingUrls({})
      setBatchId(null)
    }
    onOpenChange(next)
  }

  const parsedCount = parseLinkUrls(text).length
  const pendingCount = Object.keys(pendingUrls).length

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg gap-4 rounded-2xl"
        data-testid="knowledge-add-links-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('library.addLinksTitle')}</DialogTitle>
          <DialogDescription>
            {t('library.addLinksBody', { count: MAX_LINK_URLS })}
          </DialogDescription>
        </DialogHeader>

        <textarea
          data-testid="knowledge-add-links-textarea"
          className="min-h-[140px] w-full resize-y rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder={'https://example.com/report\nhttps://www.youtube.com/watch?v=…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={submitting}
        />

        <div className="flex items-center justify-between gap-3">
          <Text size="xs" color="muted">
            {t('library.urlCount', { count: parsedCount })}
            {pendingCount > 0 ? t('library.capturingCount', { count: pendingCount }) : ''}
          </Text>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              shape="pill"
              data-testid="knowledge-add-links-cancel"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              {pendingCount > 0 ? t('library.close') : t('library.cancel')}
            </Button>
            <Button
              type="button"
              variant="cta"
              shape="pill"
              data-testid="knowledge-add-links-submit"
              onClick={() => void handleSubmit()}
              disabled={submitting || parsedCount === 0}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('library.queuing')}
                </>
              ) : (
                t('library.addLinks')
              )}
            </Button>
          </div>
        </div>

        {results.length > 0 ? (
          <ul
            className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border p-2"
            data-testid="knowledge-add-links-results"
          >
            {results.map((row) => (
              <li
                key={row.url}
                className="flex items-start justify-between gap-2 text-xs"
              >
                <span className="min-w-0 flex-1 truncate">{row.url}</span>
                <span className="shrink-0 text-muted-foreground">
                  {pendingUrls[row.url]
                    ? t('library.capturing')
                    : statusLabel(row, t)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
